import type IPagination from 'interfaces/IPagination';
import type { FieldsWebHook, TestResult, Webhook, WebhookHistory, WebhookHistoryFilter } from 'v2/interfaces/webhook2';
import { apiServiceProxy } from 'v2/services/api-proxy';
import type { ApiServiceProxy } from 'v2/services/api-proxy';

class WebhookService {
  constructor(private readonly apiService: ApiServiceProxy) {}

  public list = () => this.apiService.get<Webhook[]>('/webhook/v2/urls').then(response => response.data);

  public historyList = (id: string, filter?: Partial<WebhookHistoryFilter>, pagination?: IPagination) => {
    const startDate = filter?.date[0];
    const endDate = filter?.date[1];

    const emailRegex: RegExp = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    const isCustomerEmail = emailRegex.test(filter?.customer);
    const customer = filter?.customer;

    return this.apiService.post<WebhookHistory[]>(`/webhook/v2/history/${id}`, {
      start_date: startDate,
      end_date: endDate,
      client_name: !isCustomerEmail && customer ? customer : undefined,
      client_email: isCustomerEmail && customer ? customer : undefined,
      entity_id: filter?.entityId || undefined,
      content_ids: filter?.product_id,
      per_page: pagination?.per_page,
      page: pagination?.current_page,
      status: filter?.status,
      events: filter?.events
    });
  };

  public unifyUrls = async () => {
    await this.apiService.post('/webhook/v2/unify-urls', {});
  };

  public hasUrlsToUnify = (): Promise<{
    allUrlsAreUnified: boolean;
    duplicatedCount: number;
  }> => {
    return this.apiService.get('/webhook/v2/has-urls-to-unify').then(response => response.data);
  };

  public resendUrl = (id: string) => {
    return this.apiService.post(`/webhook/v2/history/retry/${id}`, {});
  };

  public validateUrl = (url: string, type: string, contentType: string): Promise<TestResult> => {
    return this.apiService
      .post('/webhook/v2/test', { url, type, content_type: contentType })
      .then(response => response.data);
  };

  public getProducts = () => {
    return this.apiService.get('/webhook/v2/products');
  };

  public createURL = (data: Omit<FieldsWebHook, '_id'>) => {
    return this.apiService.post('/webhook/v2/urls', data);
  };

  public editURL = (data: FieldsWebHook) => {
    return this.apiService.put(`/webhook/v2/urls/${data._id}`, data);
  };

  public reactive = (id: string) => {
    return this.apiService.put(`/webhook/v2/urls/${id}/reactivate`, {});
  };
  public inactive = (id: string) => {
    return this.apiService.put(`/webhook/v2/urls/${id}/inactive`, {});
  };
  public removeWebhook = (id: string) => {
    return this.apiService.delete(`/webhook/v2/urls/${id}`, {});
  };
  public hasActiveUrls = () =>
    this.apiService.get<{ hasActiveUrls: boolean }>('/webhook/v2/has-active-url').then(response => response.data);
}

const webhookService = new WebhookService(apiServiceProxy);
export default webhookService;
